import React, { useState, useRef, useEffect } from "react";
import { Box, Chip, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

const SelectedFilters = ({
  filters,
  sortOption,
  removeFilter,
  clearSort,
  clearFilters,
  getEventTypeLabel,
  decorOptions = [],
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [showFade, setShowFade] = useState(false);
  const scrollRef = useRef(null);

  const hasActiveFilters =
    filters.recipient !== "All" ||
    filters.eventType !== "All" ||
    filters.decor.length > 0 ||
    filters.style !== "" ||
    sortOption !== "popularity";

  useEffect(() => {
    if (!isMobile) return;
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    const checkScroll = () => {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainer;
      if (
        scrollWidth > clientWidth &&
        scrollLeft + clientWidth < scrollWidth - 5
      ) {
        setShowFade(true);
      } else {
        setShowFade(false);
      }
    };

    checkScroll();
    scrollContainer.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);

    return () => {
      scrollContainer.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [isMobile]);

  const handleRemoveDecor = (decorId) => {
    removeFilter("decor", decorId);
  };

  if (!hasActiveFilters) return null;

  return (
    <Box sx={{ mt: 2, mb: 4, px: 2, position: "relative" }}>
      <Box
        ref={scrollRef}
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          flexWrap: isMobile ? "nowrap" : "wrap",
          overflowX: isMobile ? "auto" : "visible",
          gap: 1.5,
          py: isMobile ? 1 : 0,
          "&::-webkit-scrollbar": { height: 6 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0,0,0,0.2)",
            borderRadius: 3,
          },
        }}>
        {filters.recipient !== "All" && (
          <Chip
            label={filters.recipient}
            onDelete={() => removeFilter("recipient")}
          />
        )}

        {filters.eventType !== "All" && (
          <Chip
            label={getEventTypeLabel(filters.eventType)}
            onDelete={() => removeFilter("eventType")}
          />
        )}

        {filters.decor.length > 0 &&
          filters.decor.map((decorId) => {
            const decor = decorOptions.find((d) => d._id === decorId);
            return (
              <Chip
                key={decorId}
                label={decor ? decor.name : decorId}
                onDelete={() => handleRemoveDecor(decorId)}
              />
            );
          })}

        {filters.style && (
          <Chip label={filters.style} onDelete={() => removeFilter("style")} />
        )}

        {sortOption !== "popularity" && (
          <Chip label={`Sort: ${sortOption}`} onDelete={clearSort} />
        )}

        <Typography
          onClick={() => {
            clearFilters();
            clearSort();
          }}
          sx={{
            fontSize: 14,
            color: "primary.main",
            cursor: "pointer",
            textDecoration: "underline",
            ml: 2,
            whiteSpace: "nowrap",
            flexShrink: 0,
            "&:hover": { opacity: 0.8 },
          }}>
          Clear All
        </Typography>
      </Box>

      {isMobile && showFade && (
        <Box
          sx={{
            position: "absolute",
            right: 0,
            top: 0,
            bottom: 0,
            width: 40,
            pointerEvents: "none",
            background:
              "linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%)",
          }}
        />
      )}
    </Box>
  );
};

export default SelectedFilters;
